import React, { useRef, useEffect } from 'react';
import GraphEngine from './GraphEngine.js';

export default function GraphCanvas({ getNetwork, options, events, graph = null }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // Store props in refs so the init effect can read them without listing as deps
  const getNetworkRef = useRef(getNetwork);
  getNetworkRef.current = getNetwork;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const graphRef = useRef(graph);
  graphRef.current = graph;

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!canvas || !container) return;

    const engine = new GraphEngine(canvas, optionsRef.current);
    engineRef.current = engine;

    if (getNetworkRef.current) {
      getNetworkRef.current(engine);
    }

    // Wire events
    const eventNames = [
      'selectNode', 'selectEdge', 'hoverNode', 'blurNode',
      'hoverEdge', 'blurEdge', 'beforeDrawing', 'afterDrawing',
      'stabilized', 'dragStart', 'deselectNode', 'click', 'doubleClick',
    ];
    const wrappers = {};
    for (const name of eventNames) {
      wrappers[name] = (...args) => {
        if (eventsRef.current && eventsRef.current[name]) {
          eventsRef.current[name](...args);
        }
      };
      engine.on(name, wrappers[name]);
    }

    // Also forward selectNode as "select" (DynamicGraph uses this)
    engine.on('selectNode', (...args) => {
      if (eventsRef.current && eventsRef.current.select) {
        eventsRef.current.select(...args);
      }
    });

    // Load initial graph data if provided (DynamicGraph passes graph prop)
    const initialGraph = graphRef.current;
    if (initialGraph) {
      if (initialGraph.nodes && initialGraph.nodes.length > 0) {
        engine._nodes.add(initialGraph.nodes);
      }
      if (initialGraph.edges && initialGraph.edges.length > 0) {
        engine._edges.add(initialGraph.edges);
      }
      // Synchronous layout — one draw, then idle. Zero CPU.
      engine._physics.stabilize();
    }

    // Responsive sizing
    const ro = new ResizeObserver(() => engine._resize());
    ro.observe(container);

    engine.markDirty();

    return () => {
      ro.disconnect();
      engine.destroy();
      engineRef.current = null;
    };
  }, []); // Engine init runs once — props accessed via refs

  const height = options?.height || '100vh';
  const width = options?.width || '100%';

  return (
    <div
      ref={containerRef}
      style={{ width, height, position: 'relative', overflow: 'hidden' }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />
    </div>
  );
}
